import React, { useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import RideForm from './RideForm';

const TITLES = {
  create: 'Neue Fahrt anlegen',
  edit: 'Fahrt bearbeiten',
  assign: 'Fahrer zuweisen',
};

/**
 * Globaler Dialog, der dieselbe RideForm im eingebetteten Modus rendert –
 * für Neu anlegen, Bearbeiten und Zuweisen. Ersetzt die frühere Vollseite.
 *
 * Props:
 *  - open, mode ('create'|'edit'|'assign'), rideId, draft
 *  - dialogKey: Zähler; erzwingt frisches Remount pro Öffnung (kein State-Leak)
 *  - onClose, onSuccess
 */
const RideFormDialog = ({ open, mode, rideId, draft, dialogKey, onClose, onSuccess }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  // Erst ab md ist in der Titelzeile Platz für die Wartezeiten.
  const titleHasRoom = useMediaQuery(theme.breakpoints.up('md'));
  // Die Kurzanleitung gehört der RideForm – dort wird auch F1 verarbeitet.
  // Der Header ruft sie nur auf.
  const helpRef = useRef(null);
  // Platz in der Titelzeile, in den die RideForm die Wartezeiten per Portal
  // rendert – spart im Formular eine ganze Zeile.
  const [headerSlot, setHeaderSlot] = useState(null);

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        // Backdrop-Klick ignorieren – schützt vor versehentlichem Datenverlust bei
        // diesem großen Formular. Escape und der Schließen-Button funktionieren weiter.
        if (reason === 'backdropClick') return;
        onClose?.();
      }}
      // Anlegen/Bearbeiten ist zweispaltig und soll ohne Scrollen auskommen –
      // dafür braucht es die Breite. Das Zuweisen bleibt schmal.
      maxWidth={mode === 'assign' ? 'lg' : 'xl'}
      fullWidth
      fullScreen={fullScreen}
      scroll="paper"
      // Oben verankert statt vertikal zentriert: Sonst springt der ganze
      // Dialog, sobald Felder ein- oder ausgeblendet werden (Bestelltyp,
      // Krankenfahrt, Festpreis …), und der nächste Klick geht daneben.
      sx={{
        '& .MuiDialog-container': { alignItems: { sm: 'flex-start' } },
        '& .MuiDialog-paper': { mt: { sm: 6 }, maxHeight: { sm: 'calc(100% - 80px)' } },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, pr: 2, py: 1.25 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flexShrink: 0 }}>
          {TITLES[mode] || 'Fahrt'}
          <Tooltip title="Tastaturbedienung – Kurzanleitung (F1)">
            <IconButton
              size="small"
              onClick={() => helpRef.current?.()}
              aria-label="Kurzanleitung zur Tastaturbedienung"
            >
              <HelpOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
        {titleHasRoom && <Box ref={setHeaderSlot} sx={{ flex: 1, minWidth: 0 }} />}
        <IconButton onClick={() => onClose?.()} aria-label="Schließen" edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {/* Kein Innenabstand unten: Die Buttonleiste der RideForm schließt
          den Inhalt selbst ab (sticky), darunter wäre nur Leerraum. */}
      <DialogContent dividers sx={{ pb: 0 }}>
        {open && (
          <RideForm
            key={dialogKey}
            embedded
            mode={mode}
            rideId={rideId}
            initialDraft={draft}
            helpRef={helpRef}
            headerSlot={titleHasRoom ? headerSlot : null}
            onClose={onClose}
            onSuccess={onSuccess}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RideFormDialog;
